import { useCallback } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
export const useMassiveChargeValidator = (baseData = {}) => {
  const { t } = useTranslation();
  const validateEmail = useCallback((email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }, []);
  const validatePhone = useCallback((phone) => {
    const re = /^\+?[\d\s-]{8,}$/;
    return re.test(phone);
  }, []);
  const validateField = useCallback((key, value, row) => {
    if (value === null || value === undefined || value === '') {
      if (key === 'project' && row && row.originalProjectString && row.originalProjectString.trim().length > 0) {
        return t('reservations.massiveCharge.errors.noMatch', { value: row.originalProjectString });
      }
      if (key === 'costCenter' && row && row.originalCostCenterString && row.originalCostCenterString.trim().length > 0) {
        return t('reservations.massiveCharge.errors.noMatch', { value: row.originalCostCenterString });
      }
      if (key === 'applicant' && row && row.originalApplicantString && row.originalApplicantString.trim().length > 0) {
        return t('reservations.massiveCharge.errors.noApplicantMatch', { value: row.originalApplicantString });
      }
      if (key === 'area' && row && row.originalAreaString && row.originalAreaString.trim().length > 0) {
        return t('reservations.massiveCharge.errors.noMatch', { value: row.originalAreaString });
      }
      if (key === 'jobPosition' && row && row.originalJobPositionString && row.originalJobPositionString.trim().length > 0) {
        return t('reservations.massiveCharge.errors.noMatch', { value: row.originalJobPositionString });
      }
      return null;
    }
    switch (key) {
      case 'documentType': {
        const valStr = String(value).toLowerCase().trim();
        const validDocs = ['dni', 'pasaporte', 'carnet de extranjería', 'ce', 'passport', 'ruc'];
        return validDocs.includes(valStr) ? null : t('reservations.massiveCharge.errors.unsupportedDocument');
      }
      case 'documentNumber': {
        const cleanValue = String(value).trim();
        const docType = String(row.documentType || '').toLowerCase().trim();
        if (docType === 'dni') {
          const isNumeric = /^\d+$/.test(cleanValue);
          if (!isNumeric) return t('reservations.massiveCharge.errors.dniOnlyNumbers');
          if (cleanValue.length !== 8) return t('reservations.massiveCharge.errors.dniLength');
          return null;
        }
        return cleanValue.length >= 4 ? null : t('reservations.massiveCharge.errors.documentTooShort');
      }
      case 'reservationDate': {
        if (typeof value === 'object' && value instanceof Date) {
          const m = moment(value);
          if (!m.isValid()) return { type: 'error', message: t('reservations.massiveCharge.errors.invalidDate') };
          if (m.isBefore(moment().startOf('day'))) {
            return { type: 'error', message: t('reservations.massiveCharge.errors.noPastDates') };
          }
          if (baseData.date_range && baseData.date_range.length === 2) {
            const start = moment(baseData.date_range[0]).startOf('day');
            const end = moment(baseData.date_range[1]).endOf('day');
            if (m.isBefore(start) || m.isAfter(end)) {
              return { type: 'suggestion', message: t('reservations.massiveCharge.errors.notInCampaignRange') };
            }
          }
          return null;
        }
        if (typeof value === 'string') {
          const m = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
          if (!m.isValid()) return { type: 'error', message: t('reservations.massiveCharge.errors.invalidDateFormat') };
          if (m.isBefore(moment().startOf('day'))) {
            return { type: 'error', message: t('reservations.massiveCharge.errors.noPastDates') };
          }
          if (baseData.date_range && baseData.date_range.length === 2) {
            const start = moment(baseData.date_range[0]).startOf('day');
            const end = moment(baseData.date_range[1]).endOf('day');
            if (m.isBefore(start) || m.isAfter(end)) {
              return { type: 'suggestion', message: t('reservations.massiveCharge.errors.notInCampaignRange') };
            }
          }
          return null;
        }
        return { type: 'error', message: t('reservations.massiveCharge.errors.invalidFormat') };
      }
      case 'birthDate': {
        if (typeof value === 'object' && value instanceof Date) {
          return moment(value).isValid() ? null : t('reservations.massiveCharge.errors.invalidDate');
        }
        if (typeof value === 'string') {
          const m = moment(value, ['DD-MM-YYYY', 'YYYY-MM-DD', 'DD/MM/YYYY'], true);
          return m.isValid() ? null : t('reservations.massiveCharge.errors.invalidDateFormat');
        }
        return t('reservations.massiveCharge.errors.invalidFormat');
      }
      case 'email': {
        return validateEmail(String(value).trim()) ? null : t('reservations.massiveCharge.errors.invalidEmail');
      }
      case 'phone': {
        return validatePhone(String(value).trim()) ? null : t('reservations.massiveCharge.errors.invalidPhone');
      }
      case 'protocol': {
        if (Array.isArray(value)) {
          if (value.length === 0) {
            if (row && row._rawProtocols && row._rawProtocols.length > 0) {
              return t('reservations.massiveCharge.errors.noMatch', { value: row._rawProtocols.join(', ') });
            }
            return t('reservations.massiveCharge.errors.noProtocol');
          }
          return null;
        }
        return t('reservations.massiveCharge.errors.invalidProtocolMapping');
      }
      case 'applicant': {
        if (typeof value === 'object' && value !== null && value.id) return null;
        return t('reservations.massiveCharge.errors.invalidApplicantMapping');
      }
      case 'evaluationType': {
        if (typeof value === 'object' && value !== null && value.value) return null;
        if (typeof value === 'string' && value.trim().length > 0) return null;
        return t('reservations.massiveCharge.errors.invalidEvaluationType');
      }
      case 'names':
      case 'lastnames': {
        return String(value).trim().length >= 2 ? null : t('reservations.massiveCharge.errors.nameTooShort');
      }
      case 'costCenter':
      case 'project':
      case 'area':
      case 'jobPosition': {
        if (typeof value === 'object' && value !== null && value.id) return null;
        return t('reservations.massiveCharge.errors.invalidMappedValue');
      }
      default:
        return null;
    }
  }, [validateEmail, validatePhone, baseData.date_range, t]);
  return { validateField };
};
